import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom/client';
import './index.scss';
import PlaceComponent from "./components/place.component";
import Locations from "./Locations.jsx";
import ProductSearch from "./components/ProductSearch.jsx";
import StoreOnlyView from "./components/StoreOnlyView.jsx";
import ProductOnlyView from "./components/ProductOnlyView.jsx";
import { Provider, ErrorBoundary, LEVEL_WARN } from '@rollbar/react';
import Cookies from 'universal-cookie';
import PropTypes from 'prop-types';
import { trackPageView } from './utils/reactGaSetup';
import { setupAnalytics } from './utils/reactGaSetup';
import { loadCanonicalProducts } from './utils/productMatcher';
import { initialize as initializeSyncMatcher } from './utils/syncProductMatcher';
import { MediaDisplayProvider } from './context/MediaDisplayContext'; // Import the media display provider
import { authenticatedFetch } from './utils/authService';

// Eagerly load product data at app startup
(async () => {
  if (process.env.NODE_ENV === 'development') {         
    console.log('🚀 Preloading canonical products at app startup...');
  }
  try {
    await Promise.all([
      loadCanonicalProducts(),
      initializeSyncMatcher()
    ]);
    if (process.env.NODE_ENV === 'development') {
      console.log('✅ Canonical products preloaded successfully');
    }
  } catch (error) {
    console.error('❌ Failed to preload canonical products:', error);
  }
})();
const cookies = new Cookies();

// Initialize Google Analytics
const gaInitialized = setupAnalytics();

// Track initial page view if GA was initialized
if (gaInitialized) {
  trackPageView(window.location.pathname + window.location.search);
}

// Helper function to get shopper location from cookies
function getShopperLocationFromCookies() {
  const savedLocation = cookies.get('shopperLocation');
  if (savedLocation && savedLocation.city && savedLocation.state && savedLocation.lat && savedLocation.lng) {
    return {
      city: savedLocation.city, 
      state: savedLocation.state, 
      lat: savedLocation.lat, 
      lng: savedLocation.lng
    };
  }
  return null;
}
const rollbarConfig = {
  accessToken: process.env.REACT_APP_ROLLBAR_ACCESS_TOKEN,
  captureUncaught: true,
  captureUnhandledRejections: true,
  environment: process.env.NODE_ENV,
  server: {
    code_version: process.env.REACT_APP_VERSION || require('../package.json').version
  },
  enabled: ('production' === process.env.NODE_ENV)
};

const ErrorDisplay = ({ error, resetError }) => (
  <div>
    <h2>Oops!</h2>
    <p>Something went wrong. We've been alerted.</p>
    <p><button onClick={() => window.location.reload(false)}>Click to reload!</button></p>
    <p className="tiny">Error tracking {rollbarConfig.enabled ? 'enabled' : 'disabled'}</p>
  </div>
);

ErrorDisplay.propTypes = {
  error: PropTypes.object,
  resetError: PropTypes.func
};


function checkStatus(response) {
  if (response.status >= 200 && response.status < 300) {
    return response
  } else {
    var error = new Error(response.statusText)
    error.response = response
    throw error
  }
}

function parseJSON(response) {
  return response.json()
}

function StoreSelector(props) {
  const [shopperLocation, setShopperLocation] = useState(null);
  const [showDeliveryOptions, setShowDeliveryOptions] = useState(true);
  const [activeTab, setActiveTab] = useState('stores');
  const [initialSearchTerm, setInitialSearchTerm] = useState('');
  const abortController = React.useRef(new AbortController());

  // Check URL parameters for deep linking on component mount
  useEffect(() => {
    // Check for search parameter and path
    const urlParams = new URLSearchParams(window.location.search);
    const searchParam = urlParams.get('search');
    const locationParam = urlParams.get('location');
    const storeParam = urlParams.get('store');
    
    // Check if search param exists or tab param equals product-search
    const tabParam = urlParams.get('tab');
    const isBuyNowTab = searchParam || tabParam === 'product-search';
    
    // Check if location/store param exists or tab param equals stores
    const isStoresTab = locationParam || storeParam || tabParam === 'stores';
    
    // Check for default-tab configuration
    const defaultTabParam = urlParams.get('default-tab');
    const configDefaultTab = window.STORE_LOCATOR_CONFIG?.['default-tab'];
    const defaultTab = defaultTabParam || configDefaultTab;
    
    if (isBuyNowTab) {
      // Set the initial search term from URL parameter
      if (searchParam) {
        setInitialSearchTerm(searchParam);
      }
      // Switch to product search tab
      setActiveTab('product-search');
    } else if (isStoresTab) {
      // Switch to stores tab
      setActiveTab('stores');
    } else if (defaultTab) {
      // Use default tab if specified and no other tab is explicitly set
      if (defaultTab === 'stores') {
        setActiveTab('stores');
      } else if (defaultTab === 'products') {
        setActiveTab('product-search');
      }
    }
  }, []);

  // Load showDeliveryOptions from cookies
  useEffect(() => {
    // Always enable delivery options by default
    // This ensures delivery options are enabled regardless of cookie value
    setShowDeliveryOptions(true);
    
    // Set the cookie to true to ensure consistency
    cookies.set('showDeliveryOptions', 'true', { path: '/' });
    
    if (process.env.NODE_ENV === 'development') {
      console.log('Delivery options enabled by default');
      console.log('Current showDeliveryOptions state:', true);
    }
  }, []);

  // Load shopperLocation from deep link parameters, cookies, or auto-detect
  useEffect(() => {
    // Check URL parameters for location info
    const urlParams = new URLSearchParams(window.location.search);
    const locationParam = urlParams.get('location');
    const latParam = urlParams.get('lat');
    const lngParam = urlParams.get('lng');
    
    if (process.env.NODE_ENV === 'development') {
      console.log('Location params in URL:', { locationParam, latParam, lngParam });
    }
    
    // Case 1: We have complete location data in URL (city/state + lat/lng)
    if (locationParam && latParam && lngParam) {
      // Parse location string to get city and state
      let city = locationParam;
      let state = '';
      
      if (locationParam.includes(',')) {
        const parts = locationParam.split(',').map(part => part.trim());
        city = parts[0];
        state = parts[1] || '';
      }
      
      // Create location object with all data from URL
      const locationFromUrl = {
        city: city,
        state: state,
        lat: parseFloat(latParam),
        lng: parseFloat(lngParam)
      };
      
      if (process.env.NODE_ENV === 'development') {
        console.log('Using complete location from URL parameters:', locationFromUrl);
      }
      
      // Set location directly from URL params - no need to save to cookies
      setShopperLocation(locationFromUrl);
      return; // Exit effect to prevent cookies or auto-detect overriding deep link
    }
    // Case 2: We have only city/state in URL - geocode to get lat/lng
    else if (locationParam && !latParam && !lngParam) {
      if (process.env.NODE_ENV === 'development') {
        console.log('Deep link with only city/state found:', locationParam);
      }
      
      // Parse city and state
      let city = locationParam;
      let state = '';
      
      if (locationParam.includes(',')) {
        const parts = locationParam.split(',').map(part => part.trim());
        city = parts[0];
        state = parts[1] || '';
      }
      
      // Geocode to get lat/lng coordinates using our secure server endpoint
      const geocodeUrl = `${import.meta.env.VITE_API_URL || (import.meta.env.MODE === 'production' ? 'https://good-feels-store-selector-api.fly.dev' : 'http://localhost:3001')}/api/geocode?address=${encodeURIComponent(city)}${state ? ',' + encodeURIComponent(state) : ''}`;
      
      if (process.env.NODE_ENV === 'development') {
        console.log('Attempting geocoding for:', city, state);
        console.log('Geocoding URL:', geocodeUrl);
      }
      
      authenticatedFetch(geocodeUrl)
        .then(response => response.json())
        .then(data => {
          if (process.env.NODE_ENV === 'development') {
            console.log('Geocoding response:', data);
          }
          
          if (data.success) {
            // Create complete location object using server response
            const completeLocation = {
              city: data.city || city,
              state: data.state || state,
              lat: data.lat,
              lng: data.lng
            };
            
            // Set the location in state
            setShopperLocation(completeLocation);
            
            // Update URL with complete location data (add lat/lng params)
            urlParams.set('lat', completeLocation.lat.toString());
            urlParams.set('lng', completeLocation.lng.toString());
            const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
            window.history.replaceState(null, '', newUrl);
            
            if (process.env.NODE_ENV === 'development') {
              console.log('Updated URL with lat/lng coordinates');
            }
          } else {
            console.error('❌ Geocoding failed:', data.error || 'Unknown error');
            
            // Fallback to Boston with clear indication
            console.warn('🔄 Falling back to Boston, MA due to geocoding failure');
            const fallbackLocation = {
              city: 'Boston',
              state: 'MA',
              lat: 42.3601,
              lng: -71.0589,
              isLocationFallback: true, // Flag to indicate this is a fallback
              originalCity: city, // Remember what the user wanted
              originalState: state
            };
            
            setShopperLocation(fallbackLocation);
            
            // Update URL to show Boston instead of the failed location
            urlParams.set('location', 'Boston, MA');
            urlParams.set('lat', '42.3601');
            urlParams.set('lng', '-71.0589');
            const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
            window.history.replaceState(null, '', newUrl);
          }
        })
        .catch(error => {
          console.error('❌ Error geocoding location:', error);
          
          // Fallback to Boston with clear indication
          console.warn('🔄 Falling back to Boston, MA due to geocoding error');
          const fallbackLocation = {
            city: 'Boston',
            state: 'MA',
            lat: 42.3601,
            lng: -71.0589,
            isLocationFallback: true, // Flag to indicate this is a fallback
            originalCity: city, // Remember what the user wanted
            originalState: state
          };
          
          setShopperLocation(fallbackLocation);
          
          // Update URL to show Boston instead of the failed location
          const urlParams = new URLSearchParams(window.location.search);
          urlParams.set('location', 'Boston, MA');
          urlParams.set('lat', '42.3601');
          urlParams.set('lng', '-71.0589');
          const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
          window.history.replaceState(null, '', newUrl);
        });
      
      // Don't set initial location - wait for geocoding to complete
      return; // Exit effect, geocoding will update URL and state later
    }
    
    // Case 3: No location in URL, try cookies
    else if (!locationParam) {
      const cookieLocation = cookies.get('shopperLocation');
      
      // Check if we have valid location in cookies
      if (cookieLocation && cookieLocation.city && cookieLocation.state && 
          cookieLocation.lat && cookieLocation.lng) {
        
        if (process.env.NODE_ENV === 'development') {
          console.log('Using complete location from cookies (with coordinates):', cookieLocation);
        }
        
        // Set location from cookies
        setShopperLocation(cookieLocation);
        
        // ALWAYS update URL with complete location data from cookies
        // This prevents geocoding on future page reloads
        const currentUrlParams = new URLSearchParams(window.location.search);
        currentUrlParams.set('location', `${cookieLocation.city}, ${cookieLocation.state}`);
        currentUrlParams.set('lat', cookieLocation.lat.toString());
        currentUrlParams.set('lng', cookieLocation.lng.toString());
        
        // If no tab is set, default to stores tab since we have location
        if (!currentUrlParams.has('tab')) {
          currentUrlParams.set('tab', 'stores');
        }
        
        const newUrl = `${window.location.pathname}?${currentUrlParams.toString()}`;
        window.history.replaceState(null, '', newUrl);
        
        if (process.env.NODE_ENV === 'development') {
          console.log('Updated URL with complete location from cookies (including lat/lng to prevent future geocoding)');
        }
      }
      // If cookies don't have complete location data, proceed to auto-detection
      else {
        if (process.env.NODE_ENV === 'development') {
          console.log('Cookies missing complete location data (no coordinates), proceeding to auto-detection');
        }
        
        // Auto-detect location using StoreRocket
        if (process.env.NODE_ENV === 'development') {
          console.log('🔍 Starting StoreRocket auto-detection...');
        }
        
        fetch('https://storerocket.io/api/location')
          .then(checkStatus)
          .then(parseJSON)
          .then(function(data) {
            if (process.env.NODE_ENV === 'development') {
              console.log('📍 StoreRocket response:', data);
            }
            
            // Validate data
            if (!data.city || !data.state || !data.lat || !data.lon) {
              console.warn('⚠️ Incomplete location data from StoreRocket:', data);
              throw new Error('Incomplete location data from StoreRocket');
            }
            
            // Create location object from auto-detected data
            const newLocation = { 
              city: data.city, 
              state: data.state, 
              lat: data.lat, 
              lng: data.lon 
            };
            
            if (process.env.NODE_ENV === 'development') {
              console.log('✅ Auto-detected complete location with coordinates:', newLocation);
            }
            
            // Set location in state
            setShopperLocation(newLocation);
            
            // THIS IS THE ONLY SCENARIO WHERE WE SAVE TO COOKIES
            // We only save auto-detected location when there are no URL parameters
            cookies.set('shopperLocation', newLocation, { path: '/' });
            
            if (process.env.NODE_ENV === 'development') {
              console.log('💾 Saved complete auto-detected location (with coordinates) to cookies');
            }
            
            // ALWAYS update URL with complete location information
            // This ensures we have lat/lng in the URL for any future page reloads
            const currentUrlParams = new URLSearchParams(window.location.search);
            currentUrlParams.set('location', `${data.city}, ${data.state}`);
            currentUrlParams.set('lat', data.lat.toString());
            currentUrlParams.set('lng', data.lon.toString());
            
            // If no tab is set, default to stores tab since we have location
            if (!currentUrlParams.has('tab')) {
              currentUrlParams.set('tab', 'stores');
            }
            
            const newUrl = `${window.location.pathname}?${currentUrlParams.toString()}`;
            window.history.replaceState(null, '', newUrl);
            
            if (process.env.NODE_ENV === 'development') {
              console.log('🔗 Updated URL with auto-detected location (including lat/lng for future page loads)');
              console.log('🔗 New URL:', newUrl);
            }
          })
          .catch(function(error) {
            console.error('❌ Location auto-detection failed:', error);
            fallbackToDefaultLocation();
          });
      }
    }
  }, []);
  
  // Helper function for fallback to Boston
  const fallbackToDefaultLocation = () => {
    console.warn('Using fallback location (Boston, MA)');
    const fallbackLocation = {
      city: 'Boston',
      state: 'MA',
      lat: 42.3601,
      lng: -71.0589,
      isLocationFallback: true, // Flag to indicate this is a fallback
      originalCity: 'your location', // Generic message for auto-detection failure
      originalState: ''
    };
    setShopperLocation(fallbackLocation);
  };

  // This effect is intentionally empty
  // We no longer save location to cookies when shopperLocation changes
  // Location is ONLY saved to cookies in the auto-detection section
  useEffect(() => {
    // We don't want to save shopperLocation to cookies automatically anymore
    // It's now only saved in one place - in the StoreRocket auto-detection code
    
    if (process.env.NODE_ENV === 'development') {
      console.log('Location changed:', shopperLocation);
      console.log('NOTE: We only save location to cookies during auto-detection');
    }
    
    // Don't save showDeliveryOptions in this effect to prevent it from interfering
    // with the default value we set in the other effect
  }, [shopperLocation]);

  // Helper function to update URL with location data
  const updateUrlWithLocation = (location) => {
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.set('location', `${location.city}${location.state ? ', ' + location.state : ''}`);
    
    // Only add lat/lng if they exist and are valid
    if (location.lat !== undefined && location.lng !== undefined && 
        typeof location.lat === 'number' && typeof location.lng === 'number') {
      urlParams.set('lat', location.lat.toString());
      urlParams.set('lng', location.lng.toString());
    }
    
    const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
    window.history.replaceState(null, '', newUrl);
    
    if (process.env.NODE_ENV === 'development') {
      console.log('Updated URL with new location parameters:', location);
    }
  };

  const handlePlaceChanged = useCallback((current) => {
    const place = current.getPlace();
    if (place) {
      let city = null;
      let state = null;
      place.address_components.forEach(element => {
        if (element.types.includes('locality')) {
          city = element.long_name;
        }
        if (element.types.includes('administrative_area_level_1')) {
          state = element.short_name;
        }
      });
      
      // Ensure we have valid lat/lng values
      const lat = place.geometry?.location?.lat();
      const lng = place.geometry?.location?.lng();
      
      if (!lat || !lng) {
        console.error('Missing lat/lng in selected place:', place);
      }
      
      const newLocation = {
        city: city,
        state: state,
        lat: lat || 0,
        lng: lng || 0
      };
      
      // Set the new location in state
      setShopperLocation(newLocation);
      
      // We don't save to cookies here as this is a manually selected location
      // Only auto-detected locations are saved to cookies
      
      // Update URL with the new location (including lat/lng)
      if (activeTab === 'stores' && city) {
        // Add the location and coordinates to the URL
        updateUrlWithLocation(newLocation);
      }
    }
  }, [activeTab]);

  const storesSelected = useCallback(() => {
    setActiveTab('stores');
    
    // Update URL when tab is clicked
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      
      // Set tab parameter to stores
      url.searchParams.set('tab', 'stores');
      
      // Update the location parameter if we have one
      if (shopperLocation?.city) {
        // Add city/state to URL
        const locationStr = `${shopperLocation.city}${shopperLocation.state ? `, ${shopperLocation.state}` : ''}`;
        url.searchParams.set('location', locationStr);
        
        // Add lat/lng to URL if available
        if (shopperLocation.lat && shopperLocation.lng) {
          url.searchParams.set('lat', shopperLocation.lat.toString());
          url.searchParams.set('lng', shopperLocation.lng.toString());
        }
      } else {
        // Remove the location parameters if empty
        url.searchParams.delete('location');
        url.searchParams.delete('lat');
        url.searchParams.delete('lng');
      }
      
      // Remove any search parameter
      url.searchParams.delete('search');
      
      // Update the URL without refreshing the page
      window.history.pushState({
        tab: 'stores',
        location: shopperLocation ? shopperLocation.city : null
      }, '', url.toString());
      
    }
  }, [shopperLocation]);

  const productSearchSelected = useCallback(() => {
    setActiveTab('product-search');
    
    // Update URL when tab is clicked
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      
      // Set tab parameter to product-search
      url.searchParams.set('tab', 'product-search');
      
      // Remove location and store parameters
      url.searchParams.delete('location');
      url.searchParams.delete('store');
      
      // Keep any existing search parameter
      
      // Update the URL without refreshing the page
      window.history.pushState({
        tab: 'product-search',
        search: url.searchParams.get('search')
      }, '', url.toString());
      
    }
  }, []);

  return (
    <Provider config={rollbarConfig}>
      <ErrorBoundary level={LEVEL_WARN} fallbackUI={ErrorDisplay}>
        <div className="store-selector">
          <div className="main-tabs">
            <button 
              className={activeTab === 'stores' ? 'active-class' : ''} 
              onClick={storesSelected}
            >
              Find Stores
            </button>
            <button 
              className={activeTab === 'product-search' ? 'active-class' : ''} 
              onClick={productSearchSelected}
            >
              Product Search
            </button>
          </div>
          <br/>
          <div className="auto-complete">
            <PlaceComponent placeholder={shopperLocation} handlePlaceChanged={handlePlaceChanged}/>
          </div>
          {activeTab === 'product-search' ? (
            <ProductSearch shopperLocation={shopperLocation} initialSearchTerm={initialSearchTerm} />
          ) : (
            <Locations 
              shopperLocation={shopperLocation}
              showDeliveryOptions={showDeliveryOptions}
              integrated={true}
              limit={(getConfig().limit === null || getConfig().limit === "null" || getConfig().limit === undefined) ? null : parseInt(getConfig().limit)}
              infiniteScroll={getConfig().infiniteScroll === true}
            />
          )}
        </div>
        <p className="tiny">{process.env.NODE_ENV} - v{rollbarConfig.server.code_version}</p>
      </ErrorBoundary>
    </Provider>
  );
}


// ========================================

// Get configuration from window object
function getConfig() {
  // Check if we're in a browser environment
  if (typeof window !== 'undefined' && window.STORE_LOCATOR_CONFIG) {
    return window.STORE_LOCATOR_CONFIG;
  }
  return {};
}

// Determine which view to render based on config or URL parameter
function getViewMode() {
  const config = getConfig();
  
  // First check config
  if (config.view) {
    return config.view;
  }
  
  // Then check URL parameter
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('view') || 'full';
  }
  
  return 'full'; // Default to full view
}

const viewMode = getViewMode();
const config = getConfig();

// Prevent multiple React roots by checking if already initialized
if (window.storeLocatorInitialized) {
  if (process.env.NODE_ENV === 'development') {
    console.log('Store Locator already initialized, skipping...');
  }
} else {
  window.storeLocatorInitialized = true;
  
  const rootElement = document.getElementById("root");
  if (!rootElement) {
    console.error('No root element found for Store Locator');
  } else {
    const root = ReactDOM.createRoot(rootElement);

    // Log configuration to help with debugging only in development
    if (process.env.NODE_ENV === 'development') {
      console.log('Root config:', config);
      console.log('View mode:', viewMode);
      console.log('stockOnly setting:', config.stockOnly);
      console.log('limit setting:', config.limit);
      console.log('infiniteScroll setting:', config.infiniteScroll);
    }

    // Environment-aware logging
    if (process.env.NODE_ENV !== 'production') {
      console.log('Debug information: viewMode', viewMode, 'config', config);
    }

    // Render the appropriate component based on view mode
    if (viewMode === 'stores-only') {
      // Use the StoreOnlyView component which is designed for the embedded view
      root.render(
        <Provider config={rollbarConfig}>
          <ErrorBoundary level={LEVEL_WARN} fallbackUI={ErrorDisplay}>
            <MediaDisplayProvider>
              <StoreOnlyView />
              <p className="tiny">{process.env.NODE_ENV} - v{rollbarConfig.server.code_version}</p>
            </MediaDisplayProvider>
          </ErrorBoundary>
        </Provider>
      );
    } else if (viewMode === 'products-only') {
      // Use the ProductOnlyView component which is designed for the product search embedded view
      root.render(
        <Provider config={rollbarConfig}>
          <ErrorBoundary level={LEVEL_WARN} fallbackUI={ErrorDisplay}>
            <MediaDisplayProvider>
              <ProductOnlyView />
              <p className="tiny">{process.env.NODE_ENV} - v{rollbarConfig.server.code_version}</p>
            </MediaDisplayProvider>
          </ErrorBoundary>
        </Provider>
      );
    } else {
      // Default to the full StoreSelector
      root.render(
        <MediaDisplayProvider>
          <StoreSelector />
        </MediaDisplayProvider>
      );
    }
  } // Close the root element check
} // Close the initialization check